import { v4 as uuidv4 } from 'uuid';
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Generates a UUID for a file name
 * @returns A new UUID string
 */
export const generateFileUUID = (): string => {
  return uuidv4();
};

/**
 * Determines the asset type based on file extension
 * @param fileName The name of the file
 * @returns The asset type
 */
export const getAssetTypeFromExtension = (fileName: string): string => {
  const extension = fileName.split('.').pop()?.toLowerCase() || '';
  
  const typeMap: Record<string, string> = {
    // 3D models
    'vrm': 'vrm',
    'vrma': 'vrma',
    
    // Images
    'png': 'image',
    'jpg': 'image',
    'jpeg': 'image',
    'gif': 'image',
    'webp': 'image',
    
    // Videos
    'mp4': 'video',
    'webm': 'video',
    'ogg': 'video',
    'mov': 'video',
    'avi': 'video',
    
    // Config/JSON files
    'json': 'config',
    'yaml': 'config',
    'yml': 'config',
    
    // Data files
    'csv': 'data',
  };
  
  return typeMap[extension] || 'config'; // default to config for unknown types
};

/**
 * Validates file content type based on extension and mime type
 * @param file The file to validate
 * @param allowedTypes The allowed file types
 * @returns Validation result with isValid flag and optional error message
 */
export const validateFileType = (file: File, allowedTypes: Record<string, { allowedExtensions: string[]; maxFileSize: number; }>): { isValid: boolean; error?: string } => {
  // Extract file extension
  const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase() || '';
  
  // Check if file extension is allowed
  let isAllowed = false;
  let maxFileSize = 0;
  
  for (const [type, config] of Object.entries(allowedTypes)) {
    if (config.allowedExtensions.some(ext => ext.toLowerCase() === fileExtension.toLowerCase())) {
      isAllowed = true;
      maxFileSize = config.maxFileSize;
      
      // Validate file size against type-specific limit
      if (file.size > maxFileSize) {
        return {
          isValid: false,
          error: `File size ${formatFileSize(file.size)} exceeds maximum of ${formatFileSize(maxFileSize)} for ${type} files`
        };
      }
      
      break;
    }
  }
  
  if (!isAllowed) {
    const allExtensions = Object.values(allowedTypes).flatMap(config => config.allowedExtensions);
    return {
      isValid: false,
      error: `File type "${fileExtension}" is not supported. Supported types: ${allExtensions.join(', ')}`
    };
  }
  
  return { isValid: true };
};

/**
 * Formats file size in a human-readable format
 * @param bytes The size in bytes
 * @returns Formatted file size
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Checks if the file is a JSON file
 * @param file The file to check
 * @returns True if the file is a JSON file
 */
export const isJsonFile = (file: File): boolean => {
  const extension = file.name.split('.').pop()?.toLowerCase();
  return extension === 'json';
};